import type { GameEventsDebugEventResponse, GameEventsDebugResponse } from "libs/apiClient";
import apiURL from "libs/apiUrl";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState, type ChangeEvent, type CSSProperties, type ReactNode } from "react";
import { useParams } from "react-router";
import NotFoundPage from "./errorPages/NotFoundPage";
import SkeletonBox from "ui-kit/src/Skeleton/SkeletonBox";

type GameEventsDebugEvent = Omit<GameEventsDebugEventResponse, "details"> & {
	details?: unknown;
	playerName?: string | null;
};

type GameEventsDebugFetchError = Error & { response?: { status: number } };

function eventMatchKey(e: GameEventsDebugEvent): string {
	return JSON.stringify([e.videoTime ?? null, e.gamePlayerId ?? null, e.type ?? null]);
}

function compareEventKeys(a: string, b: string): number {
	const pa = JSON.parse(a) as [number | null, number | null, string | null];
	const pb = JSON.parse(b) as [number | null, number | null, string | null];
	const [v1, p1, t1] = pa;
	const [v2, p2, t2] = pb;
	if (v1 === null && v2 !== null) {
		return 1;
	}
	if (v1 !== null && v2 === null) {
		return -1;
	}
	if (v1 !== null && v2 !== null && v1 !== v2) {
		return v1 - v2;
	}
	if (p1 === null && p2 !== null) {
		return 1;
	}
	if (p1 !== null && p2 === null) {
		return -1;
	}
	if (p1 !== null && p2 !== null && p1 !== p2) {
		return p1 - p2;
	}
	if (t1 === null && t2 !== null) {
		return 1;
	}
	if (t1 !== null && t2 === null) {
		return -1;
	}
	if (t1 !== null && t2 !== null && t1 !== t2) {
		return t1.localeCompare(t2);
	}
	return 0;
}

function detailsMismatchBetweenV3AndTest(
	v3: GameEventsDebugEvent[],
	test: GameEventsDebugEvent[],
): boolean {
	if (v3.length === 0 || test.length === 0) {
		return false;
	}
	const setV = new Set(v3.map((e) => JSON.stringify(e.details ?? null)));
	const setT = new Set(test.map((e) => JSON.stringify(e.details ?? null)));
	if (setV.size !== setT.size) {
		return true;
	}
	for (const s of setV) {
		if (!setT.has(s)) {
			return true;
		}
	}
	return false;
}

function groupByKey(events: GameEventsDebugEvent[]): Map<string, GameEventsDebugEvent[]> {
	const m = new Map<string, GameEventsDebugEvent[]>();
	for (const e of events) {
		const k = eventMatchKey(e);
		const list = m.get(k);
		if (list === undefined) {
			m.set(k, [e]);
		} else {
			list.push(e);
		}
	}
	return m;
}

function jsonCanonical(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

function isPlainRecord(x: unknown): x is Record<string, unknown> {
	return typeof x === "object" && x !== null && !Array.isArray(x);
}

const diffStrongBase: CSSProperties = { color: "red" };
/** V3Test `details` vs Core — key only (no background); value tint for wholly-new subtrees */
const diffBlueCoreKey: CSSProperties = { color: "#0645ad" };
const diffBlueCoreValue: CSSProperties = { color: "#0645ad" };
/** Whole table row when V3Test `details` include a computed key */
const rowBgV3TestComputed: CSSProperties = { backgroundColor: "#eef5fc" };
/** Row revealed via “context” expander (hidden by filter, shown on demand) */
const rowBgFilterContext: CSSProperties = {
	backgroundColor: "#f4f7fa",
	boxShadow: "inset 3px 0 0 #64b5f6",
};
const filterContextControlStyle: CSSProperties = {
	border: "none",
	background: "transparent",
	color: "#0969da",
	cursor: "pointer",
	display: "block",
	font: "inherit",
	fontSize: 11,
	padding: 0,
	textAlign: "left",
	textDecoration: "underline",
	width: "100%",
};

const JSON_INDENT_SPACES = 6;

function jsonKeyPadding(depth: number): string {
	return " ".repeat(JSON_INDENT_SPACES * (depth + 1));
}

function jsonClosePadding(depth: number): string {
	return " ".repeat(JSON_INDENT_SPACES * depth);
}

function indentJsonContinuation(jsonPretty: string, depth: number): string {
	const pad = " ".repeat(JSON_INDENT_SPACES * (depth + 1));
	const lines = jsonPretty.split("\n");
	if (lines.length <= 1) {
		return jsonPretty;
	}
	return lines.map((line, i) => (i === 0 ? line : pad + line)).join("\n");
}

const wrapPre: CSSProperties = { whiteSpace: "pre-wrap", wordBreak: "break-word" };

const IGNORE_DIFF_JSON_KEYS = new Set<string>(["id"]);

const COMPUTED_DETAIL_KEYS = new Set<string>(["goal_type", "foul_type", "restart"]);

function detailsHasComputedTopLevelKeys(details: unknown): boolean {
	if (!isPlainRecord(details)) {
		return false;
	}
	for (const k of COMPUTED_DETAIL_KEYS) {
		if (Object.prototype.hasOwnProperty.call(details, k)) {
			return true;
		}
	}
	return false;
}

function v3TestEventsHaveComputedDetails(test: GameEventsDebugEvent[]): boolean {
	return test.some((e) => detailsHasComputedTopLevelKeys(e.details ?? null));
}

function renderBlueSubtree(vm: unknown, depth: number): ReactNode {
	return (
		<span style={{ ...diffBlueCoreValue, ...wrapPre }}>
			{indentJsonContinuation(JSON.stringify(vm, null, JSON_INDENT_SPACES), depth)}
		</span>
	);
}

function DetailsValueHighlight({
	mine,
	counterpart,
	depth = 0,
	/** Core `details` for this row — used only at root to drive blue “added vs Core” inside `details`. */
	coreDetailsBlueBase,
	/** Current Core `details` subtree for “added” keys (only in GameEventDataV3Test vs Core). */
	addedBase,
}: {
	mine: unknown;
	counterpart: unknown;
	depth?: number;
	coreDetailsBlueBase?: unknown;
	addedBase?: unknown;
}): ReactNode {
	const ds = (extra?: CSSProperties): CSSProperties => ({ ...diffStrongBase, ...extra });

	if (mine === null || mine === undefined) {
		const diff = counterpart !== null && counterpart !== undefined;
		return diff ? <strong style={ds()}>null</strong> : <>null</>;
	}

	if (Array.isArray(mine)) {
		if (addedBase !== undefined) {
			if (addedBase === null || !Array.isArray(addedBase)) {
				return (
					<span style={{ ...diffBlueCoreValue, ...wrapPre }}>
						{indentJsonContinuation(JSON.stringify(mine, null, JSON_INDENT_SPACES), depth)}
					</span>
				);
			}
			const co = Array.isArray(counterpart) ? counterpart : [];
			const itemPad = jsonKeyPadding(depth + 1);
			const closePad = jsonClosePadding(depth);
			return (
				<span style={wrapPre}>
					{"[\n"}
					{mine.map((item, i) => {
						const abItem = i < addedBase.length ? addedBase[i] : undefined;
						const voItem = i < co.length ? co[i] : undefined;
						const idxAdded = i >= addedBase.length;
						const differsItemVsV3 = i >= co.length || !jsonCanonical(item, voItem);
						const itemInner =
							idxAdded && differsItemVsV3 ? (
								<DetailsValueHighlight mine={item} counterpart={voItem} depth={depth + 1} />
							) : idxAdded ? (
								renderBlueSubtree(item, depth + 1)
							) : (
								<DetailsValueHighlight mine={item} counterpart={voItem} depth={depth + 1} addedBase={abItem} />
							);
						return (
							<span key={i}>
								{itemPad}
								{itemInner}
								{i < mine.length - 1 ? "," : ""}
								{"\n"}
							</span>
						);
					})}
					{closePad}
					{"]"}
				</span>
			);
		}
		const diff = !jsonCanonical(mine, counterpart);
		const s = indentJsonContinuation(JSON.stringify(mine, null, JSON_INDENT_SPACES), depth);
		return diff ? (
			<strong style={ds(wrapPre)}>{s}</strong>
		) : (
			<span style={wrapPre}>{s}</span>
		);
	}

	if (isPlainRecord(mine)) {
		const o = isPlainRecord(counterpart) ? counterpart : {};
		const keyPad = jsonKeyPadding(depth);
		const closePad = jsonClosePadding(depth);
		return (
			<span style={wrapPre}>
				{"{\n"}
				{Object.keys(mine).map((k) => {
					const vm = mine[k];
					const hasO = Object.prototype.hasOwnProperty.call(o, k);
					const vo = hasO ? o[k] : undefined;
					const differs =
						IGNORE_DIFF_JSON_KEYS.has(k) ? false : !hasO || !jsonCanonical(vm, vo);
					const passAdded =
						addedBase !== undefined &&
						isPlainRecord(addedBase) &&
						Object.prototype.hasOwnProperty.call(addedBase, k)
							? addedBase[k]
							: undefined;
					const missingInCore =
						addedBase !== undefined &&
						(addedBase === null ||
							!isPlainRecord(addedBase) ||
							!Object.prototype.hasOwnProperty.call(addedBase, k));
					const coreValueAtKey =
						!missingInCore && isPlainRecord(addedBase) && Object.prototype.hasOwnProperty.call(addedBase, k)
							? addedBase[k]
							: undefined;
					const differsFromCore =
						addedBase !== undefined && !missingInCore && !jsonCanonical(vm, coreValueAtKey);
					/** Blue key if Core differs; red vs V3 wins unless key is missing in Core or is a known computed field. */
					const highlightCoreKey =
						(missingInCore || differsFromCore) &&
						(!differs || missingInCore || COMPUTED_DETAIL_KEYS.has(k));
					let inner: ReactNode;
					if (depth === 0 && k === "details" && coreDetailsBlueBase !== undefined) {
						inner = (
							<DetailsValueHighlight
								mine={vm}
								counterpart={vo}
								depth={depth + 1}
								addedBase={coreDetailsBlueBase}
							/>
						);
					} else if (missingInCore && !differs) {
						inner = renderBlueSubtree(vm, depth);
					} else if (isPlainRecord(vm)) {
						if (hasO && isPlainRecord(vo)) {
							inner = (
								<DetailsValueHighlight
									mine={vm}
									counterpart={vo}
									depth={depth + 1}
									addedBase={passAdded}
								/>
							);
						} else if (!hasO) {
							inner = (
								<DetailsValueHighlight
									mine={vm}
									counterpart={{}}
									depth={depth + 1}
									addedBase={passAdded}
								/>
							);
						} else if (differs) {
							inner = (
								<strong style={{ ...diffStrongBase, ...wrapPre }}>
									{indentJsonContinuation(JSON.stringify(vm, null, JSON_INDENT_SPACES), depth)}
								</strong>
							);
						} else {
							inner = (
								<span style={wrapPre}>
									{indentJsonContinuation(JSON.stringify(vm, null, JSON_INDENT_SPACES), depth)}
								</span>
							);
						}
					} else if (Array.isArray(vm)) {
						inner = (
							<DetailsValueHighlight
								mine={vm}
								counterpart={vo ?? []}
								depth={depth}
								addedBase={passAdded}
							/>
						);
					} else if (differs) {
						inner = <strong style={ds()}>{JSON.stringify(vm)}</strong>;
					} else {
						inner = <span>{JSON.stringify(vm)}</span>;
					}
					return (
						<span key={k}>
							{keyPad}
							{highlightCoreKey ? (
								<>
									<span style={diffBlueCoreKey}>{JSON.stringify(k)}</span>
									{": "}
								</>
							) : (
								<>
									{JSON.stringify(k)}
									{": "}
								</>
							)}
							{inner}
							{"\n"}
						</span>
					);
				})}
				{closePad}
				{"}"}
			</span>
		);
	}

	const diff = !jsonCanonical(mine, counterpart);
	const s = JSON.stringify(mine);
	return diff ? <strong style={diffStrongBase}>{s}</strong> : <span>{s}</span>;
}

const ALL_TYPES_VALUE = "__all_types__";

function parseRowType(key: string): string | null {
	const parsed = JSON.parse(key) as [unknown, unknown, string | null];
	return parsed[2] ?? null;
}

function serializeEventTypeForSelect(tp: string | null): string {
	return JSON.stringify(tp);
}

function csvCell(raw: string): string {
	if (/[",\r\n]/.test(raw)) {
		return `"${raw.replace(/"/g, '""')}"`;
	}
	return raw;
}

function formatCsvScalar(v: unknown): string {
	if (v === null || v === undefined) {
		return "";
	}
	if (typeof v === "boolean") {
		return v ? "true" : "false";
	}
	return String(v);
}

function msToMinSecForCsv(ms: number | null | undefined): string {
	if (ms === null || ms === undefined) {
		return "";
	}
	const totalSec = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSec / 60);
	const seconds = totalSec % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatVideoTimeLabel(ms: number | null | undefined): string {
	if (ms === null || ms === undefined) {
		return "null";
	}
	return `${ms} (${msToMinSecForCsv(ms)})`;
}

function resolvePlayerNameFromRow(row: {
	core: GameEventsDebugEvent[];
	test: GameEventsDebugEvent[];
	v3: GameEventsDebugEvent[];
}): string | null {
	const candidates = [...row.core, ...row.test, ...row.v3];
	for (const event of candidates) {
		const name = event.playerName;
		if (typeof name === "string" && name.trim() !== "") {
			return name;
		}
	}
	return null;
}

function resolveGameTeamIdFromRow(row: {
	core: GameEventsDebugEvent[];
	test: GameEventsDebugEvent[];
	v3: GameEventsDebugEvent[];
}): number | null {
	const candidates = [...row.core, ...row.test, ...row.v3];
	for (const event of candidates) {
		if (event.gameTeamId !== null && event.gameTeamId !== undefined) {
			return event.gameTeamId;
		}
	}
	return null;
}

function detailsJsonForCsv(details: unknown): string {
	if (details === null || details === undefined) {
		return "";
	}
	try {
		return JSON.stringify(details);
	} catch {
		return "";
	}
}

function stripEventIdForCsvCompare(e: GameEventsDebugEvent): Omit<GameEventsDebugEvent, "id"> {
	const { id: _ignored, ...rest } = e;
	void _ignored;
	return rest;
}

function csvDiffCoreVsV3Test(
	c: GameEventsDebugEvent | undefined,
	t: GameEventsDebugEvent | undefined,
): boolean {
	if (c === undefined && t === undefined) {
		return false;
	}
	if (c === undefined || t === undefined) {
		return true;
	}
	return !jsonCanonical(stripEventIdForCsvCompare(c), stripEventIdForCsvCompare(t));
}

function csvDiffV3TestVsV3(
	t: GameEventsDebugEvent | undefined,
	v: GameEventsDebugEvent | undefined,
): boolean {
	if (t === undefined && v === undefined) {
		return false;
	}
	if (t === undefined || v === undefined) {
		return true;
	}
	return !jsonCanonical(stripEventIdForCsvCompare(t), stripEventIdForCsvCompare(v));
}

const GAME_EVENTS_DEBUG_CSV_HEADERS = [
	"half",
	"videoTime",
	"videoTimeMinSec",
	"footballTime",
	"footballTimeMinSec",
	"gameTeamId",
	"gamePlayerId",
	"type",
	"category",
	"success",
	"underpressure",
	"x",
	"y",
	"gameeventcore.details",
	"gameeventdatav3test.details",
	"gameeventdatav3.details",
	"diff_core_vs_v3test",
	"diff_v3test_vs_v3",
] as const;

type GameEventsDebugGroupedExportRow = {
	core: GameEventsDebugEvent[];
	test: GameEventsDebugEvent[];
	v3: GameEventsDebugEvent[];
};

function buildGameEventsDebugCsv(groupedRows: GameEventsDebugGroupedExportRow[]): string {
	const lines: string[] = [];
	lines.push(GAME_EVENTS_DEBUG_CSV_HEADERS.map(csvCell).join(","));
	for (const row of groupedRows) {
		const n = Math.max(row.core.length, row.test.length, row.v3.length);
		for (let i = 0; i < n; i++) {
			const c = row.core[i];
			const t = row.test[i];
			const v = row.v3[i];
			const ref = c ?? t ?? v;
			const values = [
				formatCsvScalar(ref?.half),
				formatCsvScalar(ref?.videoTime),
				msToMinSecForCsv(ref?.videoTime ?? null),
				formatCsvScalar(ref?.footballTime),
				msToMinSecForCsv(ref?.footballTime),
				formatCsvScalar(ref?.gameTeamId),
				formatCsvScalar(ref?.gamePlayerId),
				formatCsvScalar(ref?.type),
				formatCsvScalar(ref?.category),
				formatCsvScalar(ref?.success),
				formatCsvScalar(ref?.underPressure),
				formatCsvScalar(ref?.x),
				formatCsvScalar(ref?.y),
				detailsJsonForCsv(c?.details),
				detailsJsonForCsv(t?.details),
				detailsJsonForCsv(v?.details),
				formatCsvScalar(csvDiffCoreVsV3Test(c, t)),
				formatCsvScalar(csvDiffV3TestVsV3(t, v)),
			];
			lines.push(values.map(csvCell).join(","));
		}
	}
	return lines.join("\r\n");
}

function downloadGameEventsDebugCsv(gameId: number, groupedRows: GameEventsDebugGroupedExportRow[]): void {
	const csv = buildGameEventsDebugCsv(groupedRows);
	const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `game-${gameId}-events-debug.csv`;
	a.rel = "noopener";
	a.click();
	URL.revokeObjectURL(url);
}

function rowMatchesFilters(
	key: string,
	detailsMismatch: boolean,
	hasV3TestComputedDetails: boolean,
	typeFilterValue: string,
	onlyDetailsMismatch: boolean,
	onlyV3TestComputedDetails: boolean,
): boolean {
	if (onlyDetailsMismatch && !detailsMismatch) {
		return false;
	}
	if (onlyV3TestComputedDetails && !hasV3TestComputedDetails) {
		return false;
	}
	if (typeFilterValue === ALL_TYPES_VALUE) {
		return true;
	}
	let want: string | null;
	try {
		want = JSON.parse(typeFilterValue) as string | null;
	} catch {
		return true;
	}
	const tp = parseRowType(key);
	return tp === want;
}

const preBlockStyle: CSSProperties = {
	margin: 0,
	fontSize: 11,
	whiteSpace: "pre-wrap",
	wordBreak: "break-word",
};

function EventCell({
	entityName,
	events,
	detailsCompareEvents,
	detailsCoreForBlue,
}: {
	entityName: string;
	events: GameEventsDebugEvent[];
	detailsCompareEvents?: GameEventsDebugEvent[];
	detailsCoreForBlue?: GameEventsDebugEvent[];
}) {
	if (events.length === 0) {
		return <span style={{ color: "#888" }}>—</span>;
	}
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{events.map((e, idx) => {
				const counterpart =
					detailsCompareEvents !== undefined && detailsCompareEvents.length > 0
						? (detailsCompareEvents[idx] ?? detailsCompareEvents[0])
						: undefined;
				const showDiff = detailsCompareEvents !== undefined && counterpart !== undefined;
				const coreEv =
					detailsCoreForBlue !== undefined && detailsCoreForBlue.length > 0
						? (detailsCoreForBlue[idx] ?? detailsCoreForBlue[0])
						: undefined;
				const coreDetailsBlueBase =
					showDiff && detailsCoreForBlue !== undefined && coreEv !== undefined
						? (coreEv.details ?? null)
						: undefined;
				return (
					<div
						key={e.id !== undefined ? String(e.id) : JSON.stringify(e)}
						style={{ display: "flex", flexDirection: "column", gap: 6 }}
					>
						<div style={{ fontSize: 10, fontWeight: 600, color: "#333" }}>{entityName}</div>
						{showDiff ? (
							<div style={preBlockStyle}>
								<DetailsValueHighlight
									mine={e}
									counterpart={counterpart}
									coreDetailsBlueBase={coreDetailsBlueBase}
								/>
							</div>
						) : (
							<pre style={preBlockStyle}>{JSON.stringify(e, null, JSON_INDENT_SPACES)}</pre>
						)}
					</div>
				);
			})}
		</div>
	);
}

export function GameEventsDebugPage() {
	const { gameId: gameIdParam } = useParams();
	const gameId = gameIdParam === undefined ? NaN : parseInt(gameIdParam, 10);
	const gameIdKey = Number.isFinite(gameId) && gameId > 0 ? gameId : 0;

	const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES_VALUE);
	const [onlyDetailsMismatch, setOnlyDetailsMismatch] = useState(false);
	const [onlyV3TestComputedDetails, setOnlyV3TestComputedDetails] = useState(false);
	const [extraContextRowKeysByGameId, setExtraContextRowKeysByGameId] = useState<Record<number, string[]>>(
		{},
	);
	const extraContextRowKeys = useMemo((): string[] => {
		if (gameIdKey <= 0) {
			return [];
		}
		return extraContextRowKeysByGameId[gameIdKey] ?? [];
	}, [gameIdKey, extraContextRowKeysByGameId]);

	// Do not use client.getGameEventsDebug: generated GameStatsDebugJsonValue uses
	// `z.object({}).partial()` for objects, so Zod strips all keys in `details` at parse time.
	const { data: payload, isLoading, error } = useQuery<
		GameEventsDebugResponse,
		GameEventsDebugFetchError
	>({
		queryKey: ["gameEventsDebug", gameId],
		enabled: Number.isFinite(gameId) && gameId > 0,
		queryFn: async ({ signal }) => {
			const res = await fetch(`${apiURL}/admin/api/game-events-debug/${String(gameId)}`, {
				credentials: "include",
				signal,
			});
			if (res.status === 404) {
				const e = new Error("Not found") as Error & { response?: { status: number } };
				e.response = { status: 404 };
				throw e;
			}
			if (!res.ok) {
				const e = new Error("Request failed") as Error & { response?: { status: number } };
				e.response = { status: res.status };
				throw e;
			}
			return res.json() as Promise<GameEventsDebugResponse>;
		},
	});

	const rows = useMemo(() => {
		if (payload == null) {
			return [];
		}
		const keys = new Set<string>();
		const m3 = groupByKey(payload.gameEventDataV3);
		const mTest = groupByKey(payload.gameEventDataV3Test);
		const mCore = groupByKey(payload.gameEventCore);
		for (const k of m3.keys()) {
			keys.add(k);
		}
		for (const k of mTest.keys()) {
			keys.add(k);
		}
		for (const k of mCore.keys()) {
			keys.add(k);
		}
		const sorted = Array.from(keys).sort(compareEventKeys);
		return sorted.map((key) => {
			const v3 = m3.get(key) ?? [];
			const test = mTest.get(key) ?? [];
			const core = mCore.get(key) ?? [];
			return {
				key,
				v3,
				test,
				core,
				detailsMismatch: detailsMismatchBetweenV3AndTest(v3, test),
				hasV3TestComputedDetails: v3TestEventsHaveComputedDetails(test),
			};
		});
	}, [payload]);

	const teamAccentColorById = useMemo(() => {
		const teamIds = Array.from(
			new Set(
				rows
					.map((row) => resolveGameTeamIdFromRow(row))
					.filter((id): id is number => id !== null),
			),
		).sort((a, b) => a - b);
		const palette = ["#1e88e5", "#2e7d32"];
		const map = new Map<number, string>();
		teamIds.forEach((id, index) => {
			map.set(id, palette[index % palette.length]);
		});
		return map;
	}, [rows]);

	const typeOptions = useMemo(() => {
		const seen = new Set<string>();
		const labels: { value: string; label: string }[] = [];
		for (const row of rows) {
			const tp = parseRowType(row.key);
			const value = serializeEventTypeForSelect(tp);
			if (!seen.has(value)) {
				seen.add(value);
				labels.push({
					value,
					label: tp === null ? "(null)" : tp,
				});
			}
		}
		labels.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
		return labels;
	}, [rows]);

	const filteredRows = useMemo(
		() =>
			rows.filter((row) =>
				rowMatchesFilters(
					row.key,
					row.detailsMismatch,
					row.hasV3TestComputedDetails,
					typeFilter,
					onlyDetailsMismatch,
					onlyV3TestComputedDetails,
				),
			),
		[rows, typeFilter, onlyDetailsMismatch, onlyV3TestComputedDetails],
	);

	const rowsByKey = useMemo(() => {
		const m = new Map<string, (typeof rows)[number]>();
		for (const r of rows) {
			m.set(r.key, r);
		}
		return m;
	}, [rows]);

	const sortedRowKeys = useMemo(() => rows.map((r) => r.key), [rows]);

	const filteredRowKeySet = useMemo(() => new Set(filteredRows.map((r) => r.key)), [filteredRows]);

	const displayRows = useMemo(() => {
		const keys = new Set<string>();
		for (const r of filteredRows) {
			keys.add(r.key);
		}
		for (const k of extraContextRowKeys) {
			if (rowsByKey.has(k)) {
				keys.add(k);
			}
		}
		return Array.from(keys)
			.sort(compareEventKeys)
			.map((k) => rowsByKey.get(k))
			.filter((r): r is NonNullable<typeof r> => r !== undefined);
	}, [filteredRows, extraContextRowKeys, rowsByKey]);

	const contextRowCount = useMemo(
		() => displayRows.filter((r) => !filteredRowKeySet.has(r.key)).length,
		[displayRows, filteredRowKeySet],
	);

	const filterHidesRows = filteredRows.length < rows.length;

	const displayKeySet = useMemo(() => new Set(displayRows.map((r) => r.key)), [displayRows]);

	const keyToSortedIndex = useMemo(() => {
		const m = new Map<string, number>();
		sortedRowKeys.forEach((k, i) => {
			m.set(k, i);
		});
		return m;
	}, [sortedRowKeys]);

	const clearExtraContextForCurrentGame = useCallback(() => {
		if (gameIdKey <= 0) {
			return;
		}
		setExtraContextRowKeysByGameId((m) => {
			if (m[gameIdKey] === undefined) {
				return m;
			}
			const { [gameIdKey]: _, ...rest } = m;
			return rest;
		});
	}, [gameIdKey]);

	const revealContextKey = useCallback(
		(keyToAdd: string) => {
			if (gameIdKey <= 0) {
				return;
			}
			setExtraContextRowKeysByGameId((m) => {
				const cur = m[gameIdKey] ?? [];
				if (cur.includes(keyToAdd)) {
					return m;
				}
				return { ...m, [gameIdKey]: [...cur, keyToAdd] };
			});
		},
		[gameIdKey],
	);

	const onTypeFilterChange = useCallback(
		(e: ChangeEvent<HTMLSelectElement>) => {
			setTypeFilter(e.target.value);
			clearExtraContextForCurrentGame();
		},
		[clearExtraContextForCurrentGame],
	);

	const onOnlyDetailsMismatchChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setOnlyDetailsMismatch(e.target.checked);
			clearExtraContextForCurrentGame();
		},
		[clearExtraContextForCurrentGame],
	);

	const onOnlyV3TestComputedDetailsChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setOnlyV3TestComputedDetails(e.target.checked);
			clearExtraContextForCurrentGame();
		},
		[clearExtraContextForCurrentGame],
	);

	const exportCsv = useCallback(() => {
		const exportRows: GameEventsDebugGroupedExportRow[] = rows.map((r) => ({
			core: r.core,
			test: r.test,
			v3: r.v3,
		}));
		downloadGameEventsDebugCsv(gameId, exportRows);
	}, [gameId, rows]);

	if (!Number.isFinite(gameId) || gameId <= 0) {
		return <NotFoundPage />;
	}

	if (error) {
		const code = error?.response?.status;
		if (code === 404) {
			return <NotFoundPage />;
		}
		return (
			<div style={{ padding: 24 }}>
				<p>Failed to load ({code ?? "?"}).</p>
			</div>
		);
	}

	return (
		<div style={{ padding: "24px 32px", maxWidth: "100%" }}>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 12,
					marginBottom: 16,
				}}
			>
				<h1 style={{ margin: 0, fontSize: 22 }}>Game events debug — game {gameId}</h1>
				{!isLoading && rows.length > 0 ? (
					<button
						type="button"
						onClick={exportCsv}
						style={{
							padding: "6px 12px",
							fontSize: 13,
							cursor: "pointer",
							border: "1px solid #ccc",
							borderRadius: 6,
							background: "#fff",
						}}
					>
						Export CSV
					</button>
				) : null}
			</div>
			<p style={{ marginBottom: 16, color: "#555", maxWidth: 720 }}>
				Rows grouped by <code>videoTime</code>, <code>gamePlayerId</code>, and <code>type</code>. Each column
				shows the full <strong>GameEventCore</strong>, <strong>GameEventDataV3Test</strong>, or{" "}
				<strong>GameEventDataV3</strong> object. In V3 and V3Test columns, values that differ from the other
				column are shown in <strong style={{ color: "red" }}>red</strong>. In the V3Test column, parts of{" "}
				<code>details</code> vs <strong>GameEventCore</strong> (same row): keys only in V3Test, or with a value
				≠ Core, use a <span style={diffBlueCoreKey}>blue key</span> (color only). If the value also differs
				from V3, <strong style={{ color: "red" }}>red</strong> wins unless the key is absent in Core or is a
				known computed field (<code>goal_type</code>, <code>foul_type</code>, <code>restart</code>). When a filter
				hides rows, use <strong>↑ / ↓</strong> in the first column to reveal the adjacent hidden group.
			</p>
			{!isLoading && rows.length > 0 ? (
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						gap: 16,
						marginBottom: 16,
						padding: "12px 14px",
						background: "#f7f7f7",
						border: "1px solid #ddd",
						borderRadius: 6,
						fontSize: 13,
					}}
				>
					<label style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<span style={{ fontWeight: 600 }}>Type</span>
						<select
							value={typeFilter}
							onChange={onTypeFilterChange}
							style={{ minWidth: 200, padding: "4px 8px", fontSize: 13 }}
						>
							<option value={ALL_TYPES_VALUE}>All types</option>
							{typeOptions.map((o) => (
								<option key={o.value} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
					</label>
					<label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
						<input
							type="checkbox"
							checked={onlyDetailsMismatch}
							onChange={onOnlyDetailsMismatchChange}
						/>
						<span>Only rows with V3 / V3Test details mismatch</span>
					</label>
					<label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
						<input
							type="checkbox"
							checked={onlyV3TestComputedDetails}
							onChange={onOnlyV3TestComputedDetailsChange}
						/>
						<span>
							Only rows with computed keys in V3Test <code>details</code> (
							<code>goal_type</code>, <code>foul_type</code>, <code>restart</code>)
						</span>
					</label>
					<span style={{ color: "#555" }}>
						Showing {displayRows.length} of {rows.length} groups
						{filterHidesRows && contextRowCount > 0
							? ` (${contextRowCount} outside filter, for context)`
							: ""}
					</span>
				</div>
			) : null}
			{isLoading ? (
				<SkeletonBox style={{ width: "100%", height: 240 }} />
			) : (
				<div style={{ overflowX: "auto" }}>
					<table
						style={{
							borderCollapse: "collapse",
							width: "100%",
							fontSize: 12,
							tableLayout: "fixed",
						}}
					>
						<thead>
							<tr style={{ background: "#f0f0f0", textAlign: "left" }}>
								<th style={{ border: "1px solid #ccc", padding: 8, width: "14%" }}>videoTime / player / type</th>
								<th style={{ border: "1px solid #ccc", padding: 8, width: "28%" }}>GameEventCore</th>
								<th style={{ border: "1px solid #ccc", padding: 8, width: "29%" }}>GameEventDataV3Test</th>
								<th style={{ border: "1px solid #ccc", padding: 8, width: "29%" }}>GameEventDataV3</th>
							</tr>
						</thead>
						<tbody>
							{displayRows.length === 0 ? (
								<tr>
									<td colSpan={4} style={{ border: "1px solid #ccc", padding: 16, color: "#666" }}>
										No groups match the current filters.
									</td>
								</tr>
							) : null}
							{displayRows.map((row) => {
								const [vt, gp, tp] = JSON.parse(row.key) as [
									number | null,
									number | null,
									string | null,
								];
								const teamId = resolveGameTeamIdFromRow(row);
								const teamAccentColor = teamId === null ? "#ccc" : (teamAccentColorById.get(teamId) ?? "#ccc");
								const isContextOnlyRow = !filteredRowKeySet.has(row.key);
								const sortedIdx = keyToSortedIndex.get(row.key);
								const prevNeighborKey =
									sortedIdx !== undefined && sortedIdx > 0 ? sortedRowKeys[sortedIdx - 1] : undefined;
								const nextNeighborKey =
									sortedIdx !== undefined && sortedIdx < sortedRowKeys.length - 1
										? sortedRowKeys[sortedIdx + 1]
										: undefined;
								const canRevealPrev =
									filterHidesRows &&
									prevNeighborKey !== undefined &&
									!displayKeySet.has(prevNeighborKey);
								const canRevealNext =
									filterHidesRows &&
									nextNeighborKey !== undefined &&
									!displayKeySet.has(nextNeighborKey);
								return (
									<tr
										key={row.key}
										style={{
											verticalAlign: "top",
											...(row.detailsMismatch
												? { backgroundColor: "#fff8e1" }
												: row.hasV3TestComputedDetails
													? rowBgV3TestComputed
													: isContextOnlyRow
														? rowBgFilterContext
														: {}),
										}}
									>
										<td style={{ border: "1px solid #ccc", borderLeft: `20px solid ${teamAccentColor}`, padding: 8 }}>
											<div>videoTime: {formatVideoTimeLabel(vt)}</div>
											<div>gameTeamId: {teamId === null ? "null" : teamId}</div>
											<div>gamePlayerId: {gp === null ? "null" : gp}</div>
											<div>playerName: {resolvePlayerNameFromRow(row) ?? "null"}</div>
											<div>type: {tp === null ? "null" : JSON.stringify(tp)}</div>
											{canRevealPrev || canRevealNext ? (
												<div
													style={{
														marginTop: 10,
														paddingTop: 8,
														borderTop: "1px solid #e0e0e0",
														display: "flex",
														flexDirection: "column",
														gap: 4,
													}}
												>
													{canRevealPrev ? (
														<button
															type="button"
															style={filterContextControlStyle}
															onClick={() => revealContextKey(prevNeighborKey)}
														>
															↑ Show previous
														</button>
													) : null}
													{canRevealNext ? (
														<button
															type="button"
															style={filterContextControlStyle}
															onClick={() => revealContextKey(nextNeighborKey)}
														>
															↓ Show next
														</button>
													) : null}
												</div>
											) : null}
										</td>
										<td style={{ border: "1px solid #ccc", padding: 8 }}>
											<EventCell entityName="GameEventCore" events={row.core} />
										</td>
										<td style={{ border: "1px solid #ccc", padding: 8 }}>
											<EventCell
												entityName="GameEventDataV3Test"
												events={row.test}
												detailsCompareEvents={row.v3}
												detailsCoreForBlue={row.core}
											/>
										</td>
										<td style={{ border: "1px solid #ccc", padding: 8 }}>
											<EventCell
												entityName="GameEventDataV3"
												events={row.v3}
												detailsCompareEvents={row.test}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
